import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, DollarSign, CalendarDays, Sparkles, RefreshCw } from 'lucide-react';
import { ENDPOINTS } from './config';
import WeddingToDo from './WeddingToDo';
import WeddingBudget from './WeddingBudget';
import WeddingTimeline from './WeddingTimeline';
import TaskEditModal from './TaskEditModal';
import {
  DataErrorPanel,
  LoadingPanel,
  StaleDataNotice,
  fetchJson,
  useRemoteData,
} from './WeddingLoadState';

const WEDDING_DATE = '2027-03-27';

const getDaysUntil = (dateValue) => {
  const now = new Date();
  const target = new Date(`${dateValue}T00:00:00`);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / msPerDay));
};

const TABS = [
  { key: 'timeline', label: 'Timeline', Icon: CalendarDays },
  { key: 'todo', label: 'To-Do', Icon: CheckSquare },
  { key: 'budget', label: 'Budget', Icon: DollarSign },
];

const WeddingPlanner = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('timeline');
  // Shared task-edit modal state — opened from either Timeline or To-Do.
  // editingTask === null + modalOpen === true means "Add Task".
  const [editingTask, setEditingTask] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Everything on the page that sits *behind* a dialog. Both the task modal
  // (a sibling of this element) and the budget modals (portalled to <body>)
  // mark it `inert` while they are open, which is what their
  // `aria-modal="true"` promises. The toast container lives outside it on
  // purpose, so an error or an Undo raised by the dialog stays reachable.
  const pageContentRef = useRef(null);

  // ── Remote data ───────────────────────────────────────────────────────────
  // Both resources distinguish never-loaded / loaded / loaded-then-stale, so a
  // failed request can never fall through to empty-state copy or invented
  // totals. See WeddingLoadState.jsx.
  const loadBoard = useCallback(
    () => fetchJson(ENDPOINTS.WEDDING_TASKS, { label: 'planner' }),
    []
  );
  const board = useRemoteData(loadBoard);

  const loadBudgetSummary = useCallback(
    () => fetchJson(ENDPOINTS.WEDDING_BUDGET, { label: 'budget' }),
    []
  );
  const budget = useRemoteData(loadBudgetSummary);

  const { load: loadBoardData } = board;
  const { load: loadBudgetData } = budget;

  useEffect(() => {
    loadBoardData();
    loadBudgetData();
  }, [loadBoardData, loadBudgetData]);

  const retryAll = useCallback(() => {
    loadBoardData();
    loadBudgetData();
  }, [loadBoardData, loadBudgetData]);

  const openEditTask = (task) => {
    setEditingTask(task ?? null);
    setModalOpen(true);
  };
  const closeEditModal = () => {
    setModalOpen(false);
    setEditingTask(null);
  };

  const daysToGo = useMemo(() => getDaysUntil(WEDDING_DATE), []);

  // A task edit can shift budget-relevant work, so refresh both after a save.
  const handleTaskSaved = useCallback(() => {
    loadBoardData();
    loadBudgetData();
  }, [loadBoardData, loadBudgetData]);

  // GET /api/wedding/budget nests the percentages under `totals` — reading
  // them off the top level is what made this stat render a permanent "—".
  const budgetStat = useMemo(() => {
    if (budget.failedFirstLoad) {
      return { value: 'Error', hint: 'The budget could not be loaded. Open the Budget tab to retry.' };
    }
    if (!budget.hasData) return { value: '—', hint: 'Loading budget…' };

    const totals = budget.data.totals ?? {};
    const staleHint = budget.isStale ? ' (last known figure — refresh failed)' : '';

    if (totals.conversion_available === false) {
      const missing = (totals.unconverted_currencies ?? []).join(', ');
      return {
        value: 'N/A',
        hint: `Exchange rates unavailable${missing ? ` for ${missing}` : ''} — total cannot be calculated.`,
      };
    }
    if (typeof totals.percent_estimated === 'number') {
      return {
        value: `${Math.round(totals.percent_estimated)}%`,
        hint: `Estimated spend vs target${staleHint}`,
        stale: budget.isStale,
      };
    }
    return { value: '—', hint: 'No budget target set yet.' };
  }, [budget.data, budget.hasData, budget.isStale, budget.failedFirstLoad]);

  const taskStat = useMemo(() => {
    if (board.failedFirstLoad) {
      return { value: 'Error', hint: 'The planner could not be loaded. Use Retry below.' };
    }
    if (!board.hasData) return { value: '—', hint: 'Loading planner…' };

    const completed = board.data.summary?.completed_tasks;
    const total = board.data.summary?.total_tasks;
    if (completed == null || total == null) {
      return { value: 'Unavailable', hint: 'The server did not return task counts.' };
    }
    return {
      value: `${completed}/${total}`,
      hint: board.isStale ? 'Last known count — refresh failed.' : 'Completed / total tasks',
      stale: board.isStale,
    };
  }, [board.data, board.hasData, board.isStale, board.failedFirstLoad]);

  const summaryStats = [
    { label: 'Days to go', value: String(daysToGo), hint: `Until ${WEDDING_DATE}` },
    { label: 'Tasks', ...taskStat },
    { label: 'Budget', ...budgetStat },
  ];

  // The Budget tab owns its own request, so a planner failure must not blank
  // it out (and vice versa).
  const boardTabActive = currentTab === 'timeline' || currentTab === 'todo';

  const renderBoardTab = () => {
    if (board.phase === 'loading') return <LoadingPanel label="your planner" />;

    if (board.failedFirstLoad) {
      return (
        <DataErrorPanel
          title="We could not load your wedding planner"
          error={board.error}
          onRetry={board.load}
          retrying={board.busy}
        />
      );
    }

    return (
      <>
        {board.isStale && (
          <StaleDataNotice
            label="planner"
            error={board.error}
            onRetry={board.load}
            retrying={board.busy}
            loadedAt={board.loadedAt}
          />
        )}
        {currentTab === 'todo' && (
          <WeddingToDo board={board.data} fetchBoard={board.load} onEditTask={openEditTask} />
        )}
        {currentTab === 'timeline' && (
          <WeddingTimeline board={board.data} onEditTask={openEditTask} />
        )}
      </>
    );
  };

  return (
    <div className="font-editorial-sans min-h-screen bg-[radial-gradient(circle_at_top,_#fff8f3,_#f7efe8_36%,_#f3ede5_60%,_#ece8e4_100%)] text-stone-900">
      <div ref={pageContentRef} className="mx-auto max-w-6xl px-4 py-4 sm:px-6 lg:px-8">

        {/* ── Header card ── */}
        <section className="relative overflow-hidden rounded-[30px] border border-white/70 bg-white/80 p-4 shadow-[0_28px_80px_rgba(111,78,55,0.14)] backdrop-blur sm:rounded-[34px] sm:p-7">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(251,191,36,0.16),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(244,114,182,0.12),_transparent_30%)]" />
          <div className="relative">
            <button
              onClick={() => navigate('/')}
              className="mb-5 inline-flex min-h-[44px] items-center rounded-full border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-600 transition hover:border-stone-300 hover:text-stone-900"
            >
              ← Back to Home
            </button>

            <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
              {/* Title block */}
              <div className="max-w-xl">
                <p className="inline-flex items-center gap-2 rounded-full bg-rose-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-rose-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  Chijmes, Singapore
                </p>
                <h1 className="font-wedding-display mt-4 text-[2.8rem] font-medium leading-[0.95] tracking-[-0.03em] text-stone-900 sm:text-6xl lg:text-7xl">
                  Navjeev &amp; Kate
                </h1>
                <p className="mt-3 text-base text-stone-500 sm:text-lg">March 2027 · Singapore</p>
              </div>

              {/* Summary stat cards */}
              <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-4">
                {summaryStats.map(({ label, value, hint, stale }) => {
                  const failed = value === 'Error' || value === 'Unavailable';
                  return (
                    <div
                      key={label}
                      title={hint}
                      className={[
                        'rounded-2xl px-4 py-4 shadow-sm ring-1',
                        failed ? 'bg-red-50 ring-red-200' : 'bg-white ring-stone-200',
                      ].join(' ')}
                    >
                      <div className="text-[11px] uppercase tracking-[0.22em] text-stone-600">{label}</div>
                      <div
                        className={[
                          'mt-2 text-2xl font-semibold',
                          failed ? 'text-red-700' : 'text-stone-900',
                        ].join(' ')}
                      >
                        {value}
                      </div>
                      {failed && <div className="mt-1 text-[11px] font-medium text-red-700">{hint}</div>}
                      {!failed && stale && (
                        <div className="mt-1 text-[11px] font-medium text-amber-700">Last known</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* A single retry for both header requests, shown only while
                  something is actually broken. */}
              {(board.failedFirstLoad || budget.failedFirstLoad) && (
                <button
                  type="button"
                  onClick={retryAll}
                  disabled={board.busy || budget.busy}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-stone-900 px-5 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:opacity-70"
                >
                  <RefreshCw className={board.busy || budget.busy ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden="true" />
                  {board.busy || budget.busy ? 'Retrying…' : 'Retry loading'}
                </button>
              )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Sticky tab bar ── */}
        <div className="sticky top-0 z-20 mt-4">
          <div
            role="tablist"
            className="grid grid-cols-3 gap-1 rounded-[22px] border border-white/80 bg-white/85 p-1.5 shadow-sm ring-1 ring-stone-200 backdrop-blur sm:gap-2 sm:p-2"
          >
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                id={`wedding-tab-${key}`}
                type="button"
                role="tab"
                aria-selected={currentTab === key}
                aria-controls="wedding-tabpanel"
                onClick={() => setCurrentTab(key)}
                className={[
                  // min-h-[44px] keeps every tab at the 44px minimum touch target on mobile.
                  'flex min-h-[44px] min-w-0 items-center justify-center gap-1 rounded-[16px] px-2 py-2.5 text-[11px] font-semibold transition sm:gap-2 sm:px-4 sm:text-sm',
                  currentTab === key
                    ? 'bg-stone-900 text-white shadow'
                    : 'border border-stone-200 bg-white text-stone-600 hover:bg-stone-50',
                ].join(' ')}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab content ── */}
        <div
          id="wedding-tabpanel"
          role="tabpanel"
          aria-labelledby={`wedding-tab-${currentTab}`}
          className="mt-5"
        >
          {boardTabActive ? renderBoardTab() : <WeddingBudget backgroundRef={pageContentRef} />}
        </div>
      </div>

      <TaskEditModal
        open={modalOpen}
        task={editingTask}
        onClose={closeEditModal}
        onSaved={handleTaskSaved}
        backgroundRef={pageContentRef}
      />
    </div>
  );
};

export default WeddingPlanner;
